import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import {
    catchError,
    debounceTime,
    flatMap,
    map,
    mergeMap,
    switchMap,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import * as fromShared from '../../shared';
import {
    DisplayMessageMinimum,
    flatDeep,
    IStatusMessage,
    ResponseStatus
} from '../../shared';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { ToasterService } from '../../toaster/service';
import * as fromWeeklySummary from '../store/weekly-summary.actions';
import { TimeCardService } from '../time-card.service';
import * as fromTimecardDoc from './time-card-documentation.actions';
import * as fromDocReducer from './time-card-documentation.reducer';
import * as fromTimecard from './time-card.actions';
import * as fromTimeReducer from './time-card.reducer';

@Injectable()
export class TimeCardEffects {
    constructor(
        private actions$: Actions,
        private service: TimeCardService,
        private log: LoggingService,
        private store: Store<fromTimeReducer.State>,
        private toaster: ToasterService
    ) {}

    getTimeCards$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromTimecard.getTimeCards),
            tap(a =>
                this.log.verbose({ type: 'TimeCardEffects:getTimeCards$', a })
            ),
            switchMap(a =>
                this.service
                    .get(
                        a.EmployeeListID,
                        a.TimeCardDate,
                        a.MyDirection,
                        a.Processid,
                        a.MinEntryDate,
                        a.EndDate
                    )
                    .pipe(
                        mergeMap(tcs => [
                            fromTimecard.loadTimeCards({ timeCards: tcs })
                            // new Date(a.TimeCardDate) <= new Date(a.MinEntryDate)
                            //     ? fromWeeklySummary.getWeeklySummary({
                            //           EmployeeListID: a.EmployeeListID,
                            //           StartDate: tcs[0] != null ? new Date(tcs[0].WorkDate) : new Date(a.MinEntryDate),
                            //           EndDate: sessionStorage.getItem('mgmtEndDate') != null ? new Date(sessionStorage.getItem('mgmtEndDate')) : null
                            //       })
                            //     : { type: 'EMPTY' }
                        ]),
                        tap(x =>
                            this.log.verbose({
                                type: 'TimeCardEffects:getTimeCards$',
                                x
                            })
                        ),
                        catchError((e, c) => {
                            this.store.dispatch(
                                fromTimecard.loadTimeCards({ timeCards: [] })
                            );
                            return fromShared.Errors.handleServiceError(e, c);
                        })
                    )
            )
        )
    );

    putTimeCards$ = createEffect(() =>
        this.actions$.pipe(
            ofType(fromTimecard.putTimeCards),
            withLatestFrom(this.store.pipe(select(fromDocReducer.selectAll))),
            tap(a =>
                this.log.verbose({ type: 'TimeCardEffects:putTimeCards$', a })
            ),
            mergeMap(([a, docs]) =>
                this.service.put(a.timeCard).pipe(
                    tap(tcs => {
                        const requireSign = a.requireTimeSubmit === true;
                        this.toaster.pop(
                            'success',
                            requireSign ? 'Time saved' : 'Time saved and signed',
                            requireSign
                                ? 'Your time has been saved. Remember to sign your time when ready (Sign Week or Sign Period).'
                                : 'Your time has been saved and signed.'
                        );
                    }),
                    flatMap(tcs => {
                        const updateActions = tcs.Items.map(item => ({
                            id: item.id.toString(),
                            changes: item
                        }));
                        return [
                            fromTimecard.updateTimeCards({
                                timeCards: updateActions
                            }),
                            new fromShared.Errors.RemoveErrors(
                                tcs.Items.filter(
                                    x =>
                                        x.Status.StatusCode ===
                                        ResponseStatus.Success
                                ).map(x => x.EmpTimeID.toString())
                            ),
                            new fromShared.Errors.SetErrors(
                                [
                                    ...tcs.ErrorMessages.map(
                                        x =>
                                            ({
                                                StatusCode: ResponseStatus.None,
                                                Message: x,
                                                MsgProcessID: 0,
                                                MsgEntityID: ''
                                            } as IStatusMessage)
                                    ),
                                    ...tcs.Items.filter(
                                        x =>
                                            x &&
                                            x.Status.StatusCode >=
                                                DisplayMessageMinimum
                                    ).map(x => x.Status)
                                ].filter(fromShared.Errors.filterErrors)
                            ),
                            fromTimecardDoc.updateTimeCardDocumentations({
                                timeCardDocumentations: flatDeep(
                                    tcs.Items.map(x =>
                                        docs
                                            .filter(
                                                doc =>
                                                    doc.EmpTimeID ===
                                                    x.EmpTimeID
                                            )
                                            .map(z => ({
                                                id: `${z.EmpTimeID}-${z.WorkID}`,
                                                changes: {
                                                    EmpTimeIDHours:
                                                        x.EmployeeHours
                                                }
                                            }))
                                    )
                                )
                            })
                        ];
                    }),
                    catchError((err, c) => {
                        this.log.error({
                            type: 'TimeCardEffects:putTimeCards$',
                            error: err
                        });
                        const message =
                            err?.error?.InnerException?.ExceptionMessage ||
                            err?.error?.ExceptionMessage ||
                            err?.error?.message ||
                            err?.error?.Message ||
                            err?.message ||
                            err?.statusText ||
                            'Your time could not be saved. Please try again.';
                        this.toaster.pop(
                            'error',
                            'Time not saved',
                            message
                        );
                        return fromShared.Errors.handleServiceError(err, c);
                    })
                )
            )
        )
    );

    updateTimecard$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(fromTimecard.updateTimeCards),
            debounceTime(1000),
            tap(a =>
                this.log.verbose({ type: 'TimeCardEffects:updateTimecard$', a })
            ),
            map(a =>
                fromWeeklySummary.getWeeklySummary({
                    EmployeeListID: a.timeCards[0].changes.EmployeeListID,
                    EmpAuthID: a.timeCards[0].changes.EmpAuthID
                })
            )
        );
    });
}
